import { gql, useQuery } from '@apollo/client';
import { LOCALIZATION_DEFAULTS } from '@assets/i18n/config';
import { IntegrationProviders } from '@generated/graphql';
import type { Integration } from '@interfaces/integrations';
import type { PhysicalAddress } from '@interfaces/physicalAddress';
import { InputAdornment, MenuItem, type SxProps } from '@mui/material';
import { CountryCodes, Jurisdictions } from '@onevesthq/ov-enums';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import {
  type FieldOptions,
  type FormErrors,
  validateFields,
} from '@ovComponents/4-module/workflowCompletion/subSteps/utils';
import { getCountryName } from '@ovComponents/resources';
import { useClientContext } from '@pages/ClientDetail/clientContext';
import { useHouseholdContext } from '@pages/HouseholdDetail/householdContext';
import { UserContext } from '@providers/userContextProvider';
import { generateAddressString } from '@utils/common';
import { enumValues } from '@utils/enumValues';
import { Button, Grid, IconButton, Typography } from 'hephaestus/web';
import { Close as CloseIcon } from 'hephaestus/webIcons';
import { uniq } from 'lodash-es';
import {
  type CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../hooks/useLocalization';
import { usePermission } from '../../../hooks/usePermission';
import CanadaPostAddress from './canadaPostAddress';
import {
  DEFAULT_CANADA_POST_COUNTRIES,
  JURISDICTION_COUNTRIES,
} from './canadaPostAddress.utils';
import GoogleAddress from './googleAddress';

const FETCH_INTEGRATIONS = gql`
  query fetchIntegrations($input: FetchIntegrationsInput!) {
    fetchIntegrations(input: $input) {
      totalCount
      integrations {
        id
        provider
        type
        supportedCountries
      }
    }
  }
`;

const linkLikeButon: CSSProperties = {
  textDecoration: 'underline',
  color: 'blue',
  backgroundColor: '#ffffff00',
  borderStyle: 'none',
  fontSize: '16px',
  padding: 0,
  margin: 0,
  marginLeft: '4px',
  cursor: 'pointer',
};

export const AddressField = ({
  onChange,
  label,
  address,
  error = false,
  disabled = false,
  autoFocus,
  onFocus,
  sx,
  onBlur,
  errorText,
  infoTooltip,
  manualAddressEntry,
  defaultCountry,
  defaultAddressProvider,
  lockMessage,
  testId,
  locked,
}: {
  onChange: (e: PhysicalAddress) => void;
  label: string;
  rows?: number;
  readonly?: boolean;
  autoFocus?: boolean;
  address?: PhysicalAddress;
  error?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  onBlur?: any;
  locked?: boolean;
  onFocus?: any;
  errorText?: string;
  infoTooltip?: string;
  manualAddressEntry?: boolean;
  // Display-only default: lookup bias + dialog preselect, never written back.
  defaultCountry?: CountryCodes;
  lockMessage?: string;
  testId?: string;
  defaultAddressProvider?:
    | IntegrationProviders.Google
    | IntegrationProviders.CanadaPost;
}) => {
  const { t } = useTranslation(['components', 'pageConfiguration']);
  const { language } = useLocalization();
  const canReadIntegrations = usePermission('read:integrations');
  const { activeOrganization } = useContext(UserContext);
  const householdContext = useHouseholdContext();
  const clientContext = useClientContext();

  const [open, setOpen] = useState(false);
  const [providerFault, setProviderFault] = useState(false);
  const [errors, setErrors] = useState<FormErrors>(null);
  const [focused, setFocused] = useState<string[]>([]);
  const allowedCountries = useMemo(() => {
    // An empty list would hide the Google lookup.
    let codes =
      activeOrganization.applicableLocalization?.countries ??
      LOCALIZATION_DEFAULTS.countries ??
      [];
    if (address?.country && !codes.includes(address.country as CountryCodes))
      codes = [address.country as CountryCodes, ...codes];
    return codes;
  }, [activeOrganization.applicableLocalization?.countries, address?.country]);

  // Ignored unless the org allows it.
  const displayCountry =
    defaultCountry && allowedCountries.includes(defaultCountry)
      ? defaultCountry
      : undefined;

  const initialAddress = useMemo(
    () =>
      ({
        city: address?.city || '',
        // Unseeded default: empty stays empty in the bar.
        country: address?.country ?? undefined,
        houseNumber: address?.houseNumber || '',
        neighborhood: address?.neighborhood || '',
        postal: address?.postal || '',
        jurisdiction: address?.jurisdiction || '',
        streetName: address?.streetName || '',
        unitNumber: address?.unitNumber || '',
      }) as PhysicalAddress,
    [address],
  );
  const [physicalAddress, setPhysicalAddress] =
    useState<PhysicalAddress>(initialAddress);

  // Fixed on open so clearing can't re-scope the lookup.
  const [lookupCountry] = useState(
    () => initialAddress.country ?? displayCountry,
  );

  // Saved country, else the lookup default until picked.
  const effectiveCountry = physicalAddress.country ?? displayCountry;

  const fieldOptions: FieldOptions = useMemo(
    () => ({
      city: { required: true },
      country: { required: true },
      houseNumber: { required: true },
      neighborhood: { required: false },
      postal: { required: true },
      ...(effectiveCountry === CountryCodes.CA ||
      effectiveCountry === CountryCodes.US
        ? { jurisdiction: { required: true } }
        : {}),
      streetName: { required: true },
      unitNumber: { required: false },
    }),
    [effectiveCountry],
  );

  const organizationId =
    clientContext?.orgSettings?.id ||
    householdContext?.orgSettings?.id ||
    activeOrganization?.id;

  const { data } = useQuery(FETCH_INTEGRATIONS, {
    variables: { input: { filter: { organizationId, type: 'ADDRESS' } } },
    skip: !canReadIntegrations,
  });

  const integrationsList: Integration[] = data?.fetchIntegrations?.integrations;
  const googleIntegration = integrationsList?.find(
    (x) => x.provider === IntegrationProviders.Google,
  );
  const canadaPostIntegration = integrationsList?.find(
    (x) => x.provider === IntegrationProviders.CanadaPost,
  );

  const configuredCanadaPostCountries =
    canadaPostIntegration?.supportedCountries;
  const canadaPostCountries = useMemo(
    () =>
      configuredCanadaPostCountries?.length
        ? configuredCanadaPostCountries
        : DEFAULT_CANADA_POST_COUNTRIES,
    [configuredCanadaPostCountries],
  );

  const getAddressProvider = () => {
    if (defaultAddressProvider) return defaultAddressProvider;
    if (
      address?.country === CountryCodes.US &&
      googleIntegration &&
      !configuredCanadaPostCountries?.includes(CountryCodes.US)
    )
      return IntegrationProviders.Google;
    if (
      canadaPostIntegration &&
      (configuredCanadaPostCountries?.length ||
        allowedCountries.includes(CountryCodes.CA))
    )
      return IntegrationProviders.CanadaPost;
    return IntegrationProviders.Google;
  };
  const addressProvider = getAddressProvider();

  const manualEntryCountries = useMemo(
    () =>
      uniq(
        [
          ...allowedCountries,
          ...(addressProvider === IntegrationProviders.CanadaPost
            ? canadaPostCountries
            : []),
          physicalAddress?.country,
        ].filter((cc): cc is CountryCodes => !!cc),
      ),
    [
      allowedCountries,
      addressProvider,
      canadaPostCountries,
      physicalAddress?.country,
    ],
  );

  const validate = useCallback(
    (candidateFields?: string[], target: PhysicalAddress = physicalAddress) => {
      const newErrors = validateFields(fieldOptions, target, candidateFields);
      setErrors(newErrors);
      return newErrors;
    },
    [physicalAddress, fieldOptions],
  );

  const handleLookupFault = useCallback(() => {
    setProviderFault(true);
    setOpen(true);
  }, []);

  useEffect(() => {
    setPhysicalAddress(initialAddress);
  }, [initialAddress]);

  useEffect(() => {
    validate(focused);
  }, [validate, focused]);

  const clearAddress = () => {
    if (!generateAddressString(physicalAddress)) return;
    const emptyAddress: PhysicalAddress = {
      city: '',
      country: undefined,
      houseNumber: '',
      neighborhood: '',
      postal: '',
      jurisdiction: '',
      streetName: '',
      unitNumber: '',
    };
    setPhysicalAddress(emptyAddress);
    onChange(emptyAddress);
  };

  const submit = () => {
    // Confirming adopts the displayed default country.
    const committed = { ...physicalAddress, country: effectiveCountry };
    const formErrors = validate(undefined, committed);
    if (formErrors) {
      setFocused(Object.keys(formErrors));
      return;
    }
    setOpen(false);
    setPhysicalAddress(committed);
    onChange(committed);
  };

  const jurisdictions: Jurisdictions[] = useMemo(
    () =>
      enumValues(Jurisdictions).filter((j) =>
        j.startsWith(`${effectiveCountry}_`),
      ),
    [effectiveCountry],
  );

  const jurisdictionLabel: string = useMemo(() => {
    switch (effectiveCountry) {
      case CountryCodes.CA:
        return t('components:address.province');
      case CountryCodes.US:
        return t('components:address.state');
      default:
        return '';
    }
  }, [effectiveCountry, t]);

  const postalCodeLabel: string = useMemo(() => {
    switch (effectiveCountry) {
      case CountryCodes.CA:
        return t('components:address.postal');
      case CountryCodes.US:
        return t('components:address.zip');
      case CountryCodes.GB:
        return t('components:address.postcode');
      default:
        return t('components:address.postal');
    }
  }, [effectiveCountry, t]);

  const readOnly = Boolean(disabled || locked);

  const formButtonDisabled =
    !physicalAddress.houseNumber ||
    !physicalAddress.streetName ||
    !physicalAddress.city ||
    !effectiveCountry ||
    !physicalAddress.postal ||
    (effectiveCountry &&
      JURISDICTION_COUNTRIES.includes(effectiveCountry) &&
      !physicalAddress.jurisdiction);

  return (
    <>
      {addressProvider === IntegrationProviders.Google &&
        providerFault === false && (
          <GoogleAddress
            testId={testId ?? 'google-address'}
            label={label}
            sx={sx}
            allowedCountries={allowedCountries}
            addressString={generateAddressString(physicalAddress)}
            addressCountry={lookupCountry}
            onBlur={onBlur}
            onFocus={onFocus}
            autoFocus={autoFocus}
            error={error}
            errorText={errorText}
            disabled={disabled || locked}
            integrationId={googleIntegration?.id}
            organizationId={organizationId}
            infoTooltip={infoTooltip}
            lockMessage={lockMessage}
            openManualInput={() => setOpen(true)}
            onClear={clearAddress}
            setAddress={(item) =>
              setPhysicalAddress({
                city: item.city ?? '',
                jurisdiction: item.jurisdiction,
                streetName: item.streetName ?? '',
                postal: item.postal ?? '',
                unitNumber: item.unitNumber ?? '',
                houseNumber: item.houseNumber,
                neighborhood: item.neighborhood,
                country: item.country,
              })
            }
            onLookupFault={handleLookupFault}
          />
        )}
      {addressProvider === IntegrationProviders.CanadaPost &&
        providerFault === false && (
          <CanadaPostAddress
            testId={testId ?? 'canada-post-address'}
            label={label}
            sx={sx}
            addressString={generateAddressString(physicalAddress)}
            onBlur={onBlur}
            error={error}
            errorText={errorText}
            disabled={disabled || locked}
            infoTooltip={infoTooltip}
            integrationId={canadaPostIntegration?.id}
            organizationId={organizationId}
            supportedCountries={canadaPostCountries}
            addressCountry={lookupCountry}
            modalOpen={open}
            lockMessage={lockMessage}
            setAddress={(item) => {
              setOpen(true);
              setPhysicalAddress({
                city: item.city ?? '',
                jurisdiction: item.jurisdiction,
                streetName: item.streetName ?? '',
                postal: item.postal ?? '',
                unitNumber: item.unitNumber ?? '',
                houseNumber: item.houseNumber,
                neighborhood: item.neighborhood,
                country: item.country,
              });
            }}
            onClear={clearAddress}
            onLookupFault={handleLookupFault}
          />
        )}
      {providerFault && (
        // Lookup failed: manual entry only, but keep the bar's lock, clear-X and styling.
        <TextField
          testId={testId ? `${testId}-provider-fault` : 'provider-fault'}
          label={label}
          fullWidth
          sx={sx}
          value={generateAddressString(physicalAddress)}
          onClick={() => {
            if (!readOnly) setOpen(true);
          }}
          onBlur={onBlur}
          error={error}
          errorText={errorText}
          infoTooltip={infoTooltip}
          disabled={disabled}
          locked={readOnly}
          lockMessage={lockMessage ?? t('pageConfiguration:notEditableMessage')}
          inputProps={{ readOnly: true }}
          InputProps={
            !readOnly && generateAddressString(physicalAddress)
              ? {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label={t('components:address.clearAddress')}
                        data-testid={
                          testId
                            ? `${testId}-provider-fault-clear`
                            : 'provider-fault-clear'
                        }
                        size='small'
                        onClick={(e: React.MouseEvent) => {
                          // Clearing must not also open the manual-entry dialog.
                          e.stopPropagation();
                          clearAddress();
                        }}
                      >
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              : undefined
          }
        />
      )}
      {manualAddressEntry && (
        <Typography
          data-testid='address-manual-entry-btn'
          variant='bodyLarge'
          component='div'
          sx={{ mt: 1 }}
        >
          {t('components:address.enterYourAddressManually')}
          <button
            type='button'
            disabled={disabled || locked}
            style={linkLikeButon}
            onClick={(e) => {
              setOpen(true);
              e.preventDefault();
            }}
          >
            {`${t('components:address.here')}`}
          </button>
        </Typography>
      )}

      <Dialog onClose={() => setOpen(false)} open={open} fullWidth>
        <DialogTitle>{t('components:address.title')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid key={t('address.unitNumber') as string} size={6}>
              <TextField
                testId={testId ? `${testId}-unit-number` : 'unit-number'}
                fullWidth
                value={physicalAddress.unitNumber}
                label={t('components:address.unitNumber')}
                onChange={(e: any) =>
                  setPhysicalAddress((prevState) => ({
                    ...prevState,
                    unitNumber: e.target.value,
                  }))
                }
                onBlur={() => setFocused([...focused, 'unitNumber'])}
                errorText={errors?.unitNumber?.message}
                error={!!errors?.unitNumber}
              />
            </Grid>
            <Grid key={t('components:address.houseNumber') as string} size={6}>
              <TextField
                testId={testId ? `${testId}-house-number` : 'house-number'}
                fullWidth
                value={physicalAddress.houseNumber}
                label={t('components:address.houseNumber')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPhysicalAddress((prevState) => ({
                    ...prevState,
                    houseNumber: e.target.value,
                  }))
                }
                onBlur={() => setFocused([...focused, 'houseNumber'])}
                errorText={errors?.houseNumber?.message}
                error={!!errors?.houseNumber}
              />
            </Grid>
            <Grid key={t('components:address.streetName') as string} size={12}>
              <TextField
                testId={testId ? `${testId}-street-name` : 'street-name'}
                fullWidth
                value={physicalAddress.streetName}
                label={t('components:address.streetName')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPhysicalAddress((prevState) => ({
                    ...prevState,
                    streetName: e.target.value,
                  }))
                }
                onBlur={() => setFocused([...focused, 'streetName'])}
                errorText={errors?.streetName?.message}
                error={!!errors?.streetName}
              />
            </Grid>
            <Grid key={t('components:address.city') as string} size={6}>
              <TextField
                testId={testId ? `${testId}-city` : 'city'}
                fullWidth
                value={physicalAddress.city}
                label={t('components:address.city')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPhysicalAddress((prevState) => ({
                    ...prevState,
                    city: e.target.value,
                  }))
                }
                onBlur={() => setFocused([...focused, 'city'])}
                errorText={errors?.city?.message}
                error={!!errors?.city}
              />
            </Grid>
            <Grid size={6}>
              <SelectField
                testId={testId ? `${testId}-country` : 'country'}
                fullWidth
                value={effectiveCountry}
                label={t('components:address.country')}
                onChange={(e: any) =>
                  setPhysicalAddress((prevState) => ({
                    ...prevState,
                    country: e.target.value,
                    jurisdiction: undefined,
                  }))
                }
                onBlur={() => setFocused([...focused, 'country'])}
                errorText={errors?.country?.message}
                error={!!errors?.country}
              >
                {manualEntryCountries.map((cc) => (
                  <MenuItem
                    data-testid={
                      testId
                        ? `${testId}-country-${cc.toLowerCase()}`
                        : cc.toLowerCase()
                    }
                    key={cc}
                    value={cc}
                  >
                    {getCountryName(cc, language)}
                  </MenuItem>
                ))}
              </SelectField>
            </Grid>
            <Grid size={6}>
              {postalCodeLabel && (
                <TextField
                  testId={testId ? `${testId}-postal-code` : 'postal-code'}
                  fullWidth
                  value={physicalAddress.postal}
                  label={postalCodeLabel}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPhysicalAddress((prevState) => ({
                      ...prevState,
                      postal: e.target.value,
                    }))
                  }
                  onBlur={() => setFocused([...focused, 'postal'])}
                  errorText={errors?.postal?.message}
                  error={!!errors?.postal}
                />
              )}
            </Grid>
            <Grid size={6}>
              {jurisdictionLabel && (
                <SelectField
                  testId={testId ? `${testId}-jurisdiction` : 'jurisdiction'}
                  fullWidth
                  value={physicalAddress.jurisdiction}
                  label={jurisdictionLabel}
                  onChange={(e: any) =>
                    setPhysicalAddress((prevState) => ({
                      ...prevState,
                      jurisdiction: e.target.value,
                    }))
                  }
                  disabled={!effectiveCountry}
                  onBlur={() => setFocused([...focused, 'jurisdiction'])}
                  errorText={errors?.jurisdiction?.message}
                  error={!!errors?.jurisdiction}
                >
                  {jurisdictions.map((jurisdiction: string) => (
                    <MenuItem
                      data-testid={
                        testId
                          ? `${testId}-jurisdiction-${jurisdiction.toLowerCase()}`
                          : jurisdiction.toLowerCase()
                      }
                      key={jurisdiction}
                      value={jurisdiction}
                    >
                      {t(`geoNames:${jurisdiction}`)}
                    </MenuItem>
                  ))}
                </SelectField>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogFooter>
          <Button
            data-testid='manual-entry-confirm-button'
            variant='contained'
            onClick={submit}
            disabled={formButtonDisabled}
          >
            {t('done')}
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default AddressField;
